import type { PipelineTile, Rag, StageThresholds } from "../api";

/**
 * The four per-measure letter chips (Q/T/F/R) shown on every step tile and
 * fix-first card, each coloured by its OWN rag — never the tile's overall
 * tone (UX-mockup prototype v2, frames 1-2). Never prints the rag word.
 */
export const MEASURES = [
  { key: "quality_rag", letter: "Q", name: "Quality" },
  { key: "time_rag", letter: "T", name: "Time" },
  { key: "throughput_rag", letter: "F", name: "Flow" },
  { key: "rework_rag", letter: "R", name: "Rework" },
] as const;

export function measureChipClass(rag: Rag | string): string {
  if (rag === "red") return "bg-ks-fail/10 text-ks-fail";
  if (rag === "amber") return "bg-ks-warn/10 text-ks-warn";
  if (rag === "green") return "bg-ks-pass/10 text-ks-pass";
  return "bg-ks-grey-4 text-ks-grey-2";
}

/**
 * The rag to actually SHOW for one of a tile's four measures — `quality_rag`
 * forced to "grey" when `quality_note` is `"low_rated_sample"` (fewer than 3
 * rated samples), regardless of what the backend's own `quality_rag` says.
 * Every Q chip and the fix-first list go through this rather than reading
 * `tile.quality_rag` directly, so "too few to judge" can never render red or
 * amber (cvce-pipeline-4).
 */
export function measureRag(tile: PipelineTile, key: (typeof MEASURES)[number]["key"]): Rag {
  if (key === "quality_rag" && tile.quality_note === "low_rated_sample") return "grey";
  return tile[key];
}

export function toneDotClass(tone: "critical" | "watch" | "ok" | "neutral"): string {
  if (tone === "critical") return "bg-ks-fail";
  if (tone === "watch") return "bg-ks-warn";
  if (tone === "ok") return "bg-ks-pass";
  return "bg-ks-grey-3";
}

/** Tile border/background treatment by overall tone (mockup `.st.red/.amber/.grey`). */
export function tileToneClass(tone: "critical" | "watch" | "ok" | "neutral"): string {
  if (tone === "critical") return "border-ks-fail/60 shadow-[inset_3px_0_0_0_var(--color-ks-fail)]";
  if (tone === "watch") return "border-ks-warn/60 shadow-[inset_3px_0_0_0_var(--color-ks-warn)]";
  if (tone === "neutral") return "border-ks-grey-4 bg-ks-grey-4/40";
  return "border-ks-grey-4";
}

const GREY_REASON_TEXT: (noun: "week" | "month") => Record<string, string> = (noun) => ({
  no_feed: "No feed yet",
  stale: "Last data",
  no_data: `No data this ${noun}`,
});

/**
 * The short note line under a tile's label — grey reason, or the count that
 * fed it. `noun` names the period ("week"/"month", cvce-pipeline-4's Week |
 * Month toggle) so "No data this week" reads "No data this month" there too.
 */
export function tileNote(tile: PipelineTile, noun: "week" | "month" = "week"): string | null {
  if (tile.grey_reason) {
    const base = GREY_REASON_TEXT(noun)[tile.grey_reason] ?? tile.grey_reason;
    if (tile.grey_reason === "stale" && tile.last_event_at) {
      return `Last data ${new Date(tile.last_event_at).toLocaleDateString()}`;
    }
    if (tile.grey_reason === "no_feed" && tile.pending_on) {
      return `${base} — ${tile.pending_on}`;
    }
    return base;
  }
  if (tile.quality_note === "not_measured") return "Not measured";
  if (tile.quality_note === "low_rated_sample") return `Only ${tile.quality_n ?? "few"} rated, too few to judge`;
  if (tile.n > 0) return `${tile.n} in`;
  return null;
}

/** The "(red over X)" / "(green from X)" / "(green under X)" suffix for one measure, when its threshold is known. */
function thresholdSuffix(key: (typeof MEASURES)[number]["key"], rag: Rag, t: StageThresholds | undefined): string {
  if (!t) return "";
  if (key === "quality_rag" && t.quality_green_min != null) return ` (green from ${t.quality_green_min})`;
  if (key === "time_rag" && t.time_green_max_hours != null) return ` (green under ${t.time_green_max_hours} h)`;
  if (key === "throughput_rag" && t.throughput_amber_share != null)
    return ` (amber under ${Math.round(t.throughput_amber_share * 100)}%)`;
  if (key === "rework_rag") {
    if (rag === "red" && t.rework_amber_max != null) return ` (red over ${Math.round(t.rework_amber_max * 100)}%)`;
    if (rag === "amber" && t.rework_green_max != null)
      return ` (green under ${Math.round(t.rework_green_max * 100)}%)`;
  }
  return "";
}

/**
 * Every red/amber measure on a tile as a "letter chip + sentence" line for a
 * fix-first card — never a green or grey one (UX-mockup prototype v2, frame
 * 1: "R Rework 52% (red over 40%)", "Q Quality median 81 (green from 85)").
 * `thresholds` is optional — the sentence still reads without the "(…)"
 * suffix while the per-stage thresholds fetch is in flight.
 */
export function fixFirstMeasureLines(
  tile: PipelineTile,
  thresholds?: StageThresholds,
): { letter: string; text: string; rag: Rag }[] {
  const order: Rag[] = ["red", "amber"];
  const lines: { letter: string; text: string; rag: Rag }[] = [];
  for (const m of MEASURES) {
    const rag = measureRag(tile, m.key);
    if (rag !== "red" && rag !== "amber") continue;
    let text: string;
    if (m.key === "quality_rag") text = `Quality median ${tile.quality_value ?? "–"}`;
    else if (m.key === "time_rag")
      text =
        tile.duration_p50_ms != null ? `Time median ${(tile.duration_p50_ms / 3_600_000).toFixed(1)} h` : "Time –";
    else if (m.key === "throughput_rag") text = `Flow ${tile.n} in`;
    else text = tile.rework_rate != null ? `Rework ${Math.round(tile.rework_rate * 100)}%` : "Rework –";
    text += thresholdSuffix(m.key, rag, thresholds);
    lines.push({ letter: m.letter, text, rag });
  }
  lines.sort((a, b) => order.indexOf(a.rag) - order.indexOf(b.rag));
  return lines;
}
